/**
 * Geo detection server function.
 *
 * Reads the visitor's country from the Vercel edge geo header
 * (`x-vercel-ip-country`, an ISO 3166-1 alpha-2 code). Used to decide
 * whether to show the privacy notice banner — countries covered by the
 * CookieYes consent gate (EU-27 + UK, loaded via GTM) must NOT get the
 * notice; see PRIVACY_NOTICE_EXCLUDED_COUNTRIES in navigation.ts.
 *
 * CACHING — the part that will bite you if you touch this:
 * Every route is served with ISR (`"/**": { isr: true }` in vite.config.ts),
 * and on Vercel that layer caches by URL path — INCLUDING for POST requests
 * to `/_serverFn/*` (verified empirically: `x-vercel-cache: HIT` with a
 * growing `age` on repeated POSTs). Method choice alone does not protect a
 * server function from having its first visitor's response pinned for
 * everyone. So this handler explicitly opts its response out of both the
 * Vercel CDN cache and any shared cache via response headers. The client
 * additionally caches the result in localStorage for a day, so this
 * function is called at most ~once per visitor per day, not per pageview.
 */
import { createServerFn } from "@tanstack/react-start";
import {
  getRequestHeader,
  setResponseHeader,
} from "@tanstack/react-start/server";

export const getVisitorCountry = createServerFn({ method: "POST" }).handler(
  (): { country: string } => {
    // Per-visitor response: keep it out of the Vercel CDN cache (which the
    // blanket ISR route rule would otherwise store, even for POST) and out
    // of any shared cache.
    setResponseHeader("Cache-Control", "private, no-store");
    setResponseHeader("Vercel-CDN-Cache-Control", "no-store");
    // Vercel injects this on every request to a function. Empty locally.
    const country = getRequestHeader("x-vercel-ip-country") ?? "";
    return { country: country.toUpperCase() };
  },
);
